// File: frontend/src/pages/AboutV2/AboutV2.js

import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import styles from './AboutV2.module.css';
import { FiTarget, FiShield, FiUsers, FiCpu, FiGlobe } from 'react-icons/fi';

const AboutV2 = () => {
  const { t } = useLanguage();

  // Split paragraphs into shorter sentences
  const about = {
    intro1: 'about.intro1',
    intro2: 'about.intro2',
    approach1: 'about.approach1',
    approach2: 'about.approach2',
    flagship1: 'about.flagship1',
    flagship2: 'about.flagship2',
    ai1_1: 'about.ai1_1',
    ai1_2: 'about.ai1_2',
    closing1: 'about.closing1',
    closing2: 'about.closing2'
  };

  const values = [
    {
      icon: <FiTarget />,
      title: t('about.values.innovation_title', 'Innovation'),
      description: t('about.values.innovation_desc', 'Pushing boundaries with cutting-edge technology and fresh approaches to insurance')
    },
    {
      icon: <FiShield />,
      title: t('about.values.fairness_title', 'Fairness'),
      description: t('about.values.fairness_desc', 'Creating inclusive insurance solutions that treat everyone equitably')
    },
    {
      icon: <FiUsers />,
      title: t('about.values.community_title', 'Community'),
      description: t('about.values.community_desc', 'Building digital social communities that help reduce risk for everyone')
    },
    {
      icon: <FiCpu />,
      title: t('about.values.intelligence_title', 'Intelligence'),
      description: t('about.values.intelligence_desc', 'Leveraging AI and data analytics to create smarter insurance products')
    },
    {
      icon: <FiGlobe />,
      title: t('about.values.sustainability_title', 'Sustainability'),
      description: t('about.values.sustainability_desc', 'Developing long-term solutions that benefit customers, communities, and the planet')
    }
  ];

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.aboutContainer}>
        <div className={styles.heroSection}>
          <h1 className={styles.title}>{t('about_allegory')}</h1>
          <p className={styles.tagline}>{t('about.tagline', 'Reimagining insurance for the digital age')}</p>
        </div>

        <section className={styles.missionSection}>
          <div className={styles.sectionHeader}>
            <h2>{t('our_mission')}</h2>
            <div className={styles.underline}></div>
          </div>
          <div className={styles.paragraphContainer}>
            <p className={styles.introText}>{t(about.intro1, 'Allegory is an innovative insurance technology company on a mission to revolutionize the insurance industry.')}</p>
            <p className={styles.introText}>{t(about.intro2, 'We\'re taking insurance back to its fair and inclusive roots by rebuilding it as a fully digital business from the ground up.')}</p>
          </div>
        </section>

        <section className={styles.valuesSection}>
          <div className={styles.sectionHeader}>
            <h2>{t('about.our_values', 'Our Values')}</h2>
            <div className={styles.underline}></div>
          </div>
          <div className={styles.valuesGrid}>
            {values.map((value, index) => (
              <div key={index} className={styles.valueCard}>
                <div className={styles.valueIcon}>{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className={styles.approachSection}>
          <div className={styles.sectionHeader}>
            <h2>{t('about.our_approach', 'Our Approach')}</h2>
            <div className={styles.underline}></div>
          </div>
          <div className={styles.contentWithImage}>
            <div className={styles.content}>
              <p>{t(about.approach1, 'At Allegory, we leverage cutting-edge technology, data analytics, machine learning, and behavioral science to help reduce risks in your life.')}</p>
              <p>{t(about.approach2, 'We believe that insurance should focus on prevention and protection, not punishment or discrimination.')}</p>
              <p>{t(about.flagship1, 'Our flagship product is a mobility insurance solution delivered through a mobile application.')}</p>
              <p>{t(about.flagship2, 'We help drivers become their best selves by proactively coaching them to improve their driving skills.')}</p>
            </div>
            <div className={styles.imageContainer}>
              <div className={styles.imagePlaceholder}>
                <span>{t('about.approach_visual', 'Technology-driven insurance')}</span>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.aiSection}>
          <div className={styles.sectionHeader}>
            <h2>{t('about.ai_innovation', 'AI Innovation')}</h2>
            <div className={styles.underline}></div>
          </div>
          <div className={styles.contentWithImage}>
            <div className={styles.imageContainer}>
              <div className={styles.imagePlaceholder}>
                <span>{t('about.ai_visual', 'AI-powered insurance')}</span>
              </div>
            </div>
            <div className={styles.content}>
              <p>{t(about.ai1_1, 'Allegory is at the forefront of AI integration in the insurance industry with our advanced system, AI-1.')}</p>
              <p>{t(about.ai1_2, 'This technology is capable of managing substantial insurance portfolios and optimizing workflows to reduce operational expenses while maintaining profitability.')}</p>
            </div>
          </div>
        </section>

        <section className={styles.closingSection}>
          <div className={styles.sectionHeader}>
            <h2>{t('about.join_us', 'Join Our Journey')}</h2>
            <div className={styles.underline}></div>
          </div>
          <div className={styles.paragraphContainer}>
            <p className={styles.closingText}>{t(about.closing1, 'We invite everyone to join us on this journey to reshape the future of insurance.')}</p>
            <p className={styles.closingText}>{t(about.closing2, 'At Allegory, we\'re not just selling insurance – we\'re building a community dedicated to creating a safer, fairer, and more inclusive world of risk management.')}</p>
          </div>
          <div className={styles.ctaContainer}>
            <Link to="/news" className={styles.ctaButton}>
              {t('about.learn_more', 'Learn More')}
            </Link>
            <Link to="/contact" className={styles.ctaButtonOutline}>
              {t('about.contact_us', 'Contact Us')}
            </Link>
            <Link to="/join" className={styles.ctaButtonPrimary}>
              {t('about.join_allegory', 'Join Allegory')}
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AboutV2;